<script setup>
import { router } from '@inertiajs/vue3';
import DeleteAction from '@js/components/actions/DeleteAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import ComboboxField from '@js/components/fields/ComboboxField.vue';
import EditorField from '@js/components/fields/EditorField.vue';
import InputField from '@js/components/fields/InputField.vue';
import ListboxField from '@js/components/fields/ListboxField.vue';
import TimeField from '@js/components/fields/TimeField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormGrid from '@js/components/forms/FormGrid.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import { goBack, useRoute } from '@js/utils/route';
import { toastError, toastSaved } from '@js/utils/toast';
import { reactive, ref } from 'vue';
import { object, string } from 'yup';

const props = defineProps({
    tenants: {
        type: Array,
        required: true,
    },
    users: {
        type: Array,
        required: true,
    },
    meansOfCommunications: {
        type: Array,
        required: true,
    },
    tenant: {
        type: Object,
        required: false,
    },
    tenantAppointment: {
        type: Object,
        required: false,
    },
});

const { route } = useRoute();

const initialMinutes = props.tenantAppointment
    ? props.tenantAppointment.minutes
    : 60;

const form = reactive({
    tenant: props.tenant
        ? props.tenants.find((tenant) => tenant.id === props.tenant.id)
        : props.tenantAppointment
          ? props.tenants.find(
                (tenant) => tenant.id === props.tenantAppointment.tenantId,
            )
          : null,
    meansOfCommunication: props.tenantAppointment
        ? props.meansOfCommunications.find(
              (meansOfCommunication) =>
                  meansOfCommunication.value ===
                  props.tenantAppointment.meansOfCommunication.value,
          )
        : props.meansOfCommunications[0],
    subject: props.tenantAppointment ? props.tenantAppointment.subject : '',
    comment: props.tenantAppointment
        ? (props.tenantAppointment.comment ?? '')
        : '',
    users: props.tenantAppointment
        ? props.users.filter((user) =>
              props.tenantAppointment.userIds.includes(user.id),
          )
        : [],
    scheduledAt: props.tenantAppointment
        ? props.tenantAppointment.scheduledAt
        : null,
    hours:
        Math.floor(initialMinutes / 60) > 0
            ? String(Math.floor(initialMinutes / 60))
            : '',
    minutes: String(initialMinutes % 60).padStart(2, '0'),
});

const validationSchema = object({
    tenant: object().required('Ce champ est obligatoire.'),
    meansOfCommunication: object().required('Ce champ est obligatoire.'),
    subject: string().required('Ce champ est obligatoire.'),
    comment: string(),
    scheduledAt: string().required('Ce champ est obligatoire.'),
    hours: string(),
    minutes: string().required('Ce champ est obligatoire.'),
});

const isSubmitting = ref(false);

function submit() {
    axios
        .post(
            route(
                'front.projectManagement.apikom.postAppointment',
                props.tenantAppointment
                    ? { tenantAppointment: props.tenantAppointment.id }
                    : {},
            ),
            {
                tenantId: form.tenant.id,
                meansOfCommunication: form.meansOfCommunication.value,
                userIds: form.users.map((user) => user.id),
                subject: form.subject,
                comment: form.comment ?? '',
                scheduledAt: form.scheduledAt,
                minutes:
                    parseInt(form.hours || '0', 10) * 60 +
                    parseInt(form.minutes || '0', 10),
            },
        )

        .then(() => {
            toastSaved();

            if (props.tenant) {
                router.visit(
                    route('front.projectManagement.apikom.appointments', {
                        tenant: props.tenant.id,
                    }),
                );
            } else {
                goBack();
            }
        })
        .finally(() => {
            isSubmitting.value = false;
        })
        .catch((error) => {
            toastError(error);

            router.visit(route('front.projectManagement.apikom.appointments'));
        });
}
</script>

<template>
    <FormRoot
        :validation-schema="validationSchema"
        :submit="submit"
        v-slot="{ errors }"
    >
        <FormColumn>
            <FormContent>
                <FormGrid :columns="2">
                    <ComboboxField
                        v-model="form.tenant"
                        :options="props.tenants"
                        label="Client"
                        field-name="tenant"
                        field-id="tenant"
                        filtered-option-key="name"
                        by="id"
                        required
                        :disabled="props.tenant !== null"
                    >
                        <template #optionContent="{ option }">
                            {{ option.name }}
                        </template>
                    </ComboboxField>
                </FormGrid>

                <FormGrid :columns="2">
                    <ComboboxField
                        v-model="form.users"
                        :options="props.users"
                        label="Utilisateurs"
                        field-name="users"
                        field-id="users"
                        filteredOptionKey="fullName"
                        multiple
                    >
                        <template #optionContent="{ option }">
                            {{ option.fullName }}
                        </template>
                    </ComboboxField>
                </FormGrid>

                <FormGrid>
                    <ListboxField
                        v-model="form.meansOfCommunication"
                        field-id="meansOfCommunication"
                        field-name="meansOfCommunication"
                        label="Moyen de communication"
                        :options="props.meansOfCommunications"
                        required
                    >
                        <template #buttonContent>
                            {{ form.meansOfCommunication.label }}
                        </template>

                        <template #optionContent="{ option }">
                            {{ option.label }}
                        </template>
                    </ListboxField>

                    <InputField
                        v-model="form.scheduledAt"
                        label="Planifié le"
                        field-name="scheduledAt"
                        field-id="scheduledAt"
                        type="date"
                        required
                    />
                </FormGrid>

                <TimeField
                    v-model:hours="form.hours"
                    v-model:minutes="form.minutes"
                    hours-field-id="hours"
                    hours-field-name="hours"
                    minutes-field-id="minutes"
                    minutes-field-name="minutes"
                    label="Durée"
                    required
                />
            </FormContent>

            <FormContent>
                <InputField
                    v-model="form.subject"
                    label="Sujet"
                    field-name="subject"
                    field-id="subject"
                    required
                />

                <EditorField
                    v-model="form.comment"
                    label="Commentaire"
                    field-name="comment"
                    field-id="comment"
                />
            </FormContent>
        </FormColumn>

        <FormFooter :is-submitting="isSubmitting" :errors="errors">
            <template #actions>
                <DeleteAction
                    v-if="props.tenantAppointment"
                    :url="
                        route(
                            'front.projectManagement.apikom.deleteAppointment',
                            {
                                tenantAppointment: props.tenantAppointment.id,
                            },
                        )
                    "
                    :callback="
                        () => {
                            router.visit(
                                route(
                                    'front.projectManagement.apikom.appointments',
                                    {
                                        tenant: props.tenantAppointment
                                            .tenantId,
                                    },
                                ),
                            );
                        }
                    "
                >
                    <template #trigger>
                        <ActionButton type="secondary">
                            Supprimer
                        </ActionButton>
                    </template>
                </DeleteAction>
            </template>
        </FormFooter>
    </FormRoot>
</template>
